import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Save, X, Users as UsersIcon } from 'lucide-react';
import './ETPOS/CommonStyles.css';
import './ETPOS/AddSellStyles.css';
import './CardForm.css';
import './AddUser.css';
import apiService from '../services/api';
import useToast from '../hooks/useToast';
import { usePermissionContext } from '../contexts/PermissionContext';
import { useSubscriptionContext } from '../contexts/SubscriptionContext';
import SubscriptionExpiredModal from './Modals/SubscriptionExpiredModal';

const AddUser = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { showSuccess, showError, showWarning } = useToast();
  const { hasPermission, permissions } = usePermissionContext();
  const [isSaving, setIsSaving] = useState(false);
  const [businessId, setBusinessId] = useState(40); // Default, will be updated from userDetails
  const { isQuotaAvailable, activeSubscription } = useSubscriptionContext();
  const [showQuotaModal, setShowQuotaModal] = useState(false);
  
  // Check if we're editing a user
  const [isEditing, setIsEditing] = useState(false);
  const [editingUserId, setEditingUserId] = useState(null);
  const [roles, setRoles] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(true);

  const [formData, setFormData] = useState({
    // Personal Information
    prefix: 'mr',
    first_name: '',
    last_name: '',
    email: '',
    is_active: true,
    enable_service_staff_pin: false,

    // Roles and Permissions
    allow_login: true,
    username: '',
    password: '',
    confirm_password: '',
    role: '',

    // Sales
    cmmsn_percent: '',
    max_discount_percent: '',
    selected_contacts: false,

    // More Informations
    date_of_birth: '',
    gender: '',
    marital_status: '',
    blood_group: '',
    mobile: '',
    alternate_contact: '',
    family_contact: '',
    facebook: '',
    twitter: '',
    social_media_1: '',
    social_media_2: '',
    custom_field_1: '',
    custom_field_2: '',
    custom_field_3: '',
    custom_field_4: '',
    guardian_name: '',
    id_proof_name: '',
    id_proof_number: '',
    permanent_address: '',
    current_address: '',

    // Bank Details
    account_holder_name: '',
    account_number: '',
    bank_name: '',
    bank_identifier_code: '',
    branch: '',
    tax_payer_id: '',

    // HRM Details
    department: '',
    designation: '',

    // Payroll
    primary_work_location: '',
    basic_salary: '',
    pay_period: 'per_month',
    pay_components: '',

    // Misc
    language: 'en'
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Load roles dropdown
  useEffect(() => {
    const loadRoles = async () => {
      try {
        setLoadingRoles(true);
        const userDetails = await apiService.getUserDetails();
        const bId = userDetails?.data?.business_id || userDetails?.business_id || 40;
        setBusinessId(bId);
      /* console.log('Loading roles for business_id:', bId); */
        const response = await apiService.getRolesDropdown(bId);
      /* console.log('Roles dropdown response:', response); */
        
        if (response && Array.isArray(response)) {
          setRoles(response);
        } else if (response.data && Array.isArray(response.data)) {
          setRoles(response.data);
        } else {
      /* console.error('Invalid roles response format:', response); */
          setRoles([]);
        }
      } catch (error) {
      /* console.error('Error loading roles:', error); */
        setRoles([]);
      } finally {
        setLoadingRoles(false);
      }
    };
    
    loadRoles();
  }, []);

  // Load user data for editing
  useEffect(() => {
    if (location.state?.user) {
      const userData = location.state.user;
      /* console.log('🔥 EDIT USER - User data received:', userData); */
      /* console.log('🔥 EDIT USER - User data keys:', Object.keys(userData)); */
      setIsEditing(true);
      setEditingUserId(userData.id);
      
      // Populate form with user data
      const mappedData = {
        prefix: userData.prefix || 'mr',
        first_name: userData.first_name || '',
        last_name: userData.last_name || '',
        email: userData.email || '',
        is_active: userData.is_active !== undefined ? userData.is_active : true,
        enable_service_staff_pin: userData.enable_service_staff_pin || false,
        allow_login: userData.allow_login !== undefined ? userData.allow_login : true,
        username: userData.username || '',
        password: '',
        confirm_password: '',
        // role_id may come as number or string; role may be an object
        role: userData.role_id || (typeof userData.role === 'object' ? userData.role?.id : userData.role) || '',
        cmmsn_percent: userData.cmmsn_percent || '',
        max_discount_percent: userData.max_sales_discount_percent || userData.max_discount_percent || '',
        selected_contacts: userData.selected_contacts || false,
        // API may return dob or date_of_birth
        date_of_birth: userData.dob || userData.date_of_birth || '',
        gender: userData.gender || '',
        marital_status: userData.marital_status || '',
        blood_group: userData.blood_group || '',
        // API may return contact_number or mobile
        mobile: userData.contact_number || userData.mobile || '',
        alternate_contact: userData.alt_number || userData.alternate_contact || '',
        family_contact: userData.family_number || userData.family_contact || '',
        facebook: userData.fb_link || userData.facebook || '',
        twitter: userData.twitter_link || userData.twitter || '',
        social_media_1: userData.social_media_1 || '',
        social_media_2: userData.social_media_2 || '',
        custom_field_1: userData.custom_field_1 || '',
        custom_field_2: userData.custom_field_2 || '',
        custom_field_3: userData.custom_field_3 || '',
        custom_field_4: userData.custom_field_4 || '',
        guardian_name: userData.guardian_name || '',
        id_proof_name: userData.id_proof_name || '',
        id_proof_number: userData.id_proof_number || '',
        // Bank details: may be nested under bank_details or flat
        account_holder_name: userData.account_holder_name || userData.bank_details?.account_holder_name || '',
        account_number: userData.account_number || userData.bank_details?.account_number || '',
        bank_name: userData.bank_name || userData.bank_details?.bank_name || '',
        bank_identifier_code: userData.bank_identifier_code || userData.bank_details?.bank_identifier_code || '',
        branch: userData.branch || userData.bank_details?.branch || '',
        tax_payer_id: userData.tax_payer_id || userData.bank_details?.tax_payer_id || '',
        // Department/designation may come as ID or nested object or department_id
        department: String(userData.department_id || (typeof userData.department === 'object' ? userData.department?.id : userData.department) || ''),
        designation: String(userData.designation_id || (typeof userData.designation === 'object' ? userData.designation?.id : userData.designation) || ''),
        language: userData.language || 'en',
        primary_work_location: userData.primary_work_location || '',
        permanent_address: userData.permanent_address || '',
        current_address: userData.current_address || '',
        basic_salary: userData.basic_salary || '',
        pay_period: userData.pay_period || 'per_month',
        pay_components: userData.pay_components || ''
      };
      /* console.log('🔥 EDIT USER - Mapped form data:', mappedData); */
      setFormData(mappedData);
    }
  }, [location.state]);

  const handleSubmit = async (e) => {
      /* console.log('🔥 HANDLE SUBMIT CALLED - Event:', e); */
    e.preventDefault();
      /* console.log('🔥 Form submitted:', formData); */
    
    // Manual validation
    if (!formData.first_name || !formData.first_name.trim()) {
      showError('First Name is required!');
      return;
    }
    
    if (formData.allow_login && (!formData.username || !formData.username.trim())) {
      showError('Username is required!');
      return;
    }
    
    if (!formData.role) {
      showError('Please select a User Role!');
      return;
    }

    if (formData.allow_login && !isEditing && (!formData.password || !formData.password.trim())) {
      showError('Password is required!');
      return;
    }
    
    if (formData.allow_login && !isEditing && formData.password !== formData.confirm_password) {
      showError('Password and Confirm Password do not match!');
      return;
    }
    
    // Check permissions before proceeding
    if (isEditing && !hasPermission('user.update') && permissions.length > 0) {
      showError('You do not have permission to update users');
      return;
    }
    
    if (!isEditing && !hasPermission('user.create') && permissions.length > 0) {
      showError('You do not have permission to create users');
      return;
    }
      /* console.log('ADD USER FORM - Validation and permissions passed, proceeding with API call...'); */
    setIsSaving(true);
    
    // Map form data to backend expected field names
    const apiData = {
      business_id: businessId,
      prefix: formData.prefix,
      first_name: formData.first_name,
      last_name: formData.last_name || null,
      email: formData.email || null,
      username: formData.username,
      password: formData.password || null,
      password_confirmation: formData.confirm_password || null,
      role_id: formData.role ? parseInt(formData.role) : null,
      is_active: formData.is_active ? 1 : 0,
      allow_login: formData.allow_login ? 1 : 0,
      enable_service_staff_pin: formData.enable_service_staff_pin ? 1 : 0,
      cmmsn_percent: parseFloat(formData.cmmsn_percent) || 0,
      max_sales_discount_percent: parseFloat(formData.max_discount_percent) || 0,
      selected_contacts: formData.selected_contacts ? 1 : 0,
      dob: formData.date_of_birth || null,
      gender: formData.gender || null,
      marital_status: formData.marital_status || null,
      blood_group: formData.blood_group || null,
      contact_number: formData.mobile || null,
      alt_number: formData.alternate_contact || null,
      family_number: formData.family_contact || null,
      fb_link: formData.facebook || null,
      twitter_link: formData.twitter || null,
      social_media_1: formData.social_media_1 || null,
      social_media_2: formData.social_media_2 || null,
      custom_field_1: formData.custom_field_1 || null,
      custom_field_2: formData.custom_field_2 || null,
      custom_field_3: formData.custom_field_3 || null,
      custom_field_4: formData.custom_field_4 || null,
      guardian_name: formData.guardian_name || null,
      id_proof_name: formData.id_proof_name || null,
      id_proof_number: formData.id_proof_number || null,
      permanent_address: formData.permanent_address || null,
      current_address: formData.current_address || null,
      bank_details: {
        account_holder_name: formData.account_holder_name || null,
        account_number: formData.account_number || null,
        bank_name: formData.bank_name || null,
        bank_identifier_code: formData.bank_identifier_code || null,
        branch: formData.branch || null,
        tax_payer_id: formData.tax_payer_id || null
      },
      department_id: formData.department ? parseInt(formData.department) || null : null,
      designation_id: formData.designation ? parseInt(formData.designation) || null : null,
      location_id: formData.primary_work_location ? parseInt(formData.primary_work_location) || null : null,
      basic_salary: parseFloat(formData.basic_salary) || 0,
      pay_period: formData.pay_period || 'per_month',
      pay_components: formData.pay_components || null
    };
      /* console.log('ADD USER FORM - Mapped API data:', apiData); */
    
    // ─── Subscription quota check (only for new users, not edits) ───
    if (!isEditing) {
      const quotaOk = isQuotaAvailable('user_count');
      if (!quotaOk) {
        setShowQuotaModal(true);
        return;
      }
    }
    
    try {
      let response;
      if (isEditing) {
      /* console.log('ADD USER FORM - Updating user with ID:', editingUserId); */
        response = await apiService.updateUser(editingUserId, apiData);
      /* console.log('ADD USER FORM - User updated successfully:', response); */
        
        // Navigate back with success state
        navigate('/users', { state: { activeTab: 'users', userUpdated: true } });
      } else {
      /* console.log('ADD USER FORM - Creating new user...'); */
        response = await apiService.createUser(apiData);
      /* console.log('ADD USER FORM - User created successfully:', response); */
        // Navigate back immediately with success state
        navigate('/users', { state: { activeTab: 'users', userCreated: true } });
      }
    } catch (error) {
      /* console.error('ADD USER FORM - Error saving user:', error); */
      showError('Failed to save user: ' + (error.message || 'Unknown error'));
    } finally {
      setIsSaving(false);
    }
  };
  return (
    <div className="sell-form-container">
      <SubscriptionExpiredModal
        isOpen={showQuotaModal}
        onClose={() => setShowQuotaModal(false)}
        type="quota"
        limitName="Users"
        current={activeSubscription?.users_created || 0}
        allowed={activeSubscription?.package_details?.user_count || 0}
        packageName={activeSubscription?.package_details?.name || 'Current Plan'}
      />
      {/* Header */}
      <div className="page-header">
        <button
          type="button"
          onClick={() => navigate('/users', { state: { activeTab: 'users' } })}
          className="back-button"
          style={{
            background: 'white',
            border: '1px solid #e2e8f0',
            borderRadius: '8px',
            padding: '0.5rem 0.75rem',
            cursor: 'pointer',
            fontSize: '1.2rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: '40px',
            height: '40px'
          }}
          title="Back to Users List"
        >
          ←
        </button>
        <h2 style={{ fontSize: '1.25rem' }}>
          {isEditing ? 'Edit User' : 'Add User'}
        </h2>
      </div>

      <form id="userForm" onSubmit={handleSubmit} className="sell-form">

        {/* 1. User Information */}
        <div className="form-section">
          <h3>User Information</h3>
          <div className="form-row" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
            <div className="form-group">
              <label>Prefix</label>
              <select className="form-select" name="prefix" value={formData.prefix} onChange={handleChange}>
                <option value="mr">Mr</option>
                <option value="mrs">Mrs</option>
                <option value="miss">Miss</option>
              </select>
            </div>
            <div className="form-group">
              <label>First Name *</label>
              <input type="text" className="form-input" name="first_name" value={formData.first_name} onChange={handleChange} placeholder="First name" />
            </div>
            <div className="form-group">
              <label>Last Name</label>
              <input type="text" className="form-input" name="last_name" value={formData.last_name} onChange={handleChange} placeholder="Last name" />
            </div>
            <div className="form-group">
              <label>Email</label>
              <input type="email" className="form-input" name="email" value={formData.email} onChange={handleChange} placeholder="email@example.com" />
            </div>
          </div>
          <div className="form-row" style={{ gridTemplateColumns: 'repeat(4, 1fr)', marginTop: '0.5rem' }}>
            <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <input type="checkbox" id="is_active" name="is_active" checked={!!formData.is_active} onChange={handleChange} />
              <label htmlFor="is_active" style={{ margin: 0, background: 'none', color: 'inherit', userSelect: 'none' }}>Is active?</label>
            </div>
            <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <input type="checkbox" id="enable_service_staff_pin" name="enable_service_staff_pin" checked={!!formData.enable_service_staff_pin} onChange={handleChange} />
              <label htmlFor="enable_service_staff_pin" style={{ margin: 0, background: 'none', color: 'inherit', userSelect: 'none' }}>Enable service staff pin</label>
            </div>
          </div>
        </div>

        {/* 2. Roles and Permissions */}
        <div className="form-section">
          <h3>Roles and Permissions</h3>
          <div className="form-row" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
            <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <input type="checkbox" id="allow_login" name="allow_login" checked={!!formData.allow_login} onChange={handleChange} />
              <label htmlFor="allow_login" style={{ margin: 0, background: 'none', color: 'inherit', userSelect: 'none' }}>Allow login</label>
            </div>
            {formData.allow_login && (
              <>
                <div className="form-group">
                  <label>Username</label>
                  <input type="text" className="form-input" name="username" value={formData.username} onChange={handleChange} placeholder="Leave blank to auto generate" />
                </div>
                {!isEditing && (
                  <>
                    <div className="form-group">
                      <label>Password *</label>
                      <input type="password" className="form-input" name="password" value={formData.password} onChange={handleChange} />
                    </div>
                    <div className="form-group">
                      <label>Confirm Password *</label>
                      <input
                        type="password"
                        className="form-input"
                        name="confirm_password"
                        value={formData.confirm_password}
                        onChange={handleChange}
                        style={{
                          borderColor: formData.confirm_password && formData.password !== formData.confirm_password ? '#ef4444' : '',
                          boxShadow: formData.confirm_password && formData.password !== formData.confirm_password ? '0 0 0 1px #ef4444' : ''
                        }}
                      />
                      {formData.confirm_password && formData.password !== formData.confirm_password && (
                        <span style={{ color: '#ef4444', fontSize: '0.75rem', marginTop: '0.25rem', display: 'block' }}>
                          Passwords do not match
                        </span>
                      )}
                    </div>
                  </>
                )}
              </>
            )}
          </div>
          <div className="form-row" style={{ gridTemplateColumns: 'repeat(4, 1fr)', marginTop: '0.5rem' }}>
            <div className="form-group">
              <label>Role *</label>
              <select className="form-select" name="role" value={formData.role} onChange={handleChange} disabled={loadingRoles}>
                <option value="">Please Select</option>
                {roles.map(role => (<option key={role.id} value={role.id}>{role.name}</option>))}
              </select>
            </div>
          </div>
        </div>

        {/* 3. Sales */}
        <div className="form-section">
          <h3>Sales</h3>
          <div className="form-row" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
            <div className="form-group">
              <label>Sales Commission (%)</label>
              <input type="number" className="form-input" name="cmmsn_percent" value={formData.cmmsn_percent} onChange={handleChange} placeholder="0" />
            </div>
            <div className="form-group">
              <label>Max discount (%)</label>
              <input type="number" className="form-input" name="max_discount_percent" value={formData.max_discount_percent} onChange={handleChange} placeholder="0" />
            </div>
            <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <input type="checkbox" id="selected_contacts" name="selected_contacts" checked={!!formData.selected_contacts} onChange={handleChange} />
              <label htmlFor="selected_contacts" style={{ margin: 0 }}>Allow Selected Contacts</label>
            </div>
          </div>
        </div>

        {/* 4. More Informations */}
        <div className="form-section">
          <h3>More Informations</h3>
          <div className="form-row" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
            <div className="form-group">
              <label>Date of Birth</label>
              <input type="date" className="form-input" name="date_of_birth" value={formData.date_of_birth} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>Gender</label>
              <select className="form-select" name="gender" value={formData.gender} onChange={handleChange}>
                <option value="">Select</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div className="form-group">
              <label>Marital Status</label>
              <select className="form-select" name="marital_status" value={formData.marital_status} onChange={handleChange}>
                <option value="">Select</option>
                <option value="single">Single</option>
                <option value="married">Married</option>
              </select>
            </div>
            <div className="form-group">
              <label>Blood Group</label>
              <input type="text" className="form-input" name="blood_group" value={formData.blood_group} onChange={handleChange} placeholder="e.g. A+" />
            </div>
            <div className="form-group">
              <label>Mobile</label>
              <input type="text" className="form-input" name="mobile" value={formData.mobile} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>Alternate Contact</label>
              <input type="text" className="form-input" name="alternate_contact" value={formData.alternate_contact} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>Family Contact</label>
              <input type="text" className="form-input" name="family_contact" value={formData.family_contact} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>Facebook</label>
              <input type="text" className="form-input" name="facebook" value={formData.facebook} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>Twitter Link</label>
              <input type="text" className="form-input" name="twitter" value={formData.twitter} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>Social Media 1</label>
              <input type="text" className="form-input" name="social_media_1" value={formData.social_media_1} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>Social Media 2</label>
              <input type="text" className="form-input" name="social_media_2" value={formData.social_media_2} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>Custom field 1</label>
              <input type="text" className="form-input" name="custom_field_1" value={formData.custom_field_1} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>Custom field 2</label>
              <input type="text" className="form-input" name="custom_field_2" value={formData.custom_field_2} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>Custom field 3</label>
              <input type="text" className="form-input" name="custom_field_3" value={formData.custom_field_3} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>Custom field 4</label>
              <input type="text" className="form-input" name="custom_field_4" value={formData.custom_field_4} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>Guardian Name</label>
              <input type="text" className="form-input" name="guardian_name" value={formData.guardian_name} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>ID proof name</label>
              <input type="text" className="form-input" name="id_proof_name" value={formData.id_proof_name} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>ID proof number</label>
              <input type="text" className="form-input" name="id_proof_number" value={formData.id_proof_number} onChange={handleChange} />
            </div>
          </div>
          <div className="form-row" style={{ gridTemplateColumns: 'repeat(2, 1fr)', marginTop: '0.75rem' }}>
            <div className="form-group">
              <label>Permanent Address</label>
              <textarea className="form-textarea" name="permanent_address" value={formData.permanent_address} onChange={handleChange} rows="2" />
            </div>
            <div className="form-group">
              <label>Current Address</label>
              <textarea className="form-textarea" name="current_address" value={formData.current_address} onChange={handleChange} rows="2" />
            </div>
          </div>
        </div>

        {/* 5. Bank Details */}
        <div className="form-section">
          <h3>Bank Details</h3>
          <div className="form-row" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
            <div className="form-group">
              <label>Account Holder's Name</label>
              <input type="text" className="form-input" name="account_holder_name" value={formData.account_holder_name} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>Account Number</label>
              <input type="text" className="form-input" name="account_number" value={formData.account_number} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>Bank Name</label>
              <input type="text" className="form-input" name="bank_name" value={formData.bank_name} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>Bank Identifier Code</label>
              <input type="text" className="form-input" name="bank_identifier_code" value={formData.bank_identifier_code} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>Branch</label>
              <input type="text" className="form-input" name="branch" value={formData.branch} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>Tax Payer ID</label>
              <input type="text" className="form-input" name="tax_payer_id" value={formData.tax_payer_id} onChange={handleChange} />
            </div>
          </div>
        </div>

        {/* 6. HRM Details */}
        <div className="form-section">
          <h3>HRM Details</h3>
          <div className="form-row" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
            <div className="form-group">
              <label>Department</label>
              <select className="form-select" name="department" value={formData.department} onChange={handleChange}>
                <option value="">Please Select</option>
                <option value="1">Operations</option>
                <option value="2">Sales</option>
                <option value="3">Support</option>
              </select>
            </div>
            <div className="form-group">
              <label>Designation</label>
              <select className="form-select" name="designation" value={formData.designation} onChange={handleChange}>
                <option value="">Please Select</option>
                <option value="1">Manager</option>
                <option value="2">Staff</option>
                <option value="3">Admin</option>
              </select>
            </div>
          </div>
        </div>

        {/* 7. Payroll */}
        <div className="form-section">
          <h3>Payroll</h3>
          <div className="form-row" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
            <div className="form-group">
              <label>Primary work location</label>
              <select className="form-select" name="primary_work_location" value={formData.primary_work_location} onChange={handleChange}>
                <option value="">Please Select</option>
                <option value="1">Location 1</option>
                <option value="2">Location 2</option>
              </select>
            </div>
            <div className="form-group">
              <label>Basic salary</label>
              <input type="number" className="form-input" name="basic_salary" value={formData.basic_salary} onChange={handleChange} placeholder="0.00" />
            </div>
            <div className="form-group">
              <label>Pay Period</label>
              <select className="form-select" name="pay_period" value={formData.pay_period} onChange={handleChange}>
                <option value="per_month">Per Month</option>
                <option value="per_week">Per Week</option>
              </select>
            </div>
            <div className="form-group">
              <label>Pay Components</label>
              <input type="text" className="form-input" name="pay_components" value={formData.pay_components} onChange={handleChange} />
            </div>
          </div>
        </div>

        {/* Submit Actions */}
        <div className="action-buttons-section">
          <div className="action-buttons">
            <button type="button" className="save-print-btn" onClick={() => navigate('/users')} disabled={isSaving}>Cancel</button>
            <button type="submit" className="save-btn" disabled={isSaving}>
              {isSaving ? 'Saving...' : 'Save User'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default AddUser;